/**
 * Hidden pictures — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const hiddenPicturesPack: ToilePack = {
    slug: 'hidden-pictures',
    category: 'coloring',
    name: 'Hidden pictures',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'picnic', label: 'Picnic', subject: 'a family picnic in a park, with a spread blanket, a wicker basket, a kite and a duck pond'},
        {key: 'bedroom', label: 'Bedroom', subject: "a child's untidy bedroom, with a bunk bed, a spilling toy chest, shelves of books and a curtained window"},
        {key: 'farmyard', label: 'Farmyard', subject: 'a busy farmyard, with an open barn, a tractor, a fence line, stacked hay bales and chickens underfoot'},
    ],
    styles: [
        {key: 'simple', label: 'Young child', style: 'Drawn for a young child: the objects fairly large and near plain sight, bold even outlines, wide areas left open to color', palette: 'Black ink only', framing: 'The scene centred with a white margin above the key strip.'},
        {key: 'classic', label: 'Classic', style: 'Drawn the classic magazine way: the objects disguised as scenery, a toothbrush in the fence, a sock in the tree, at medium line weight', palette: 'Black ink only', framing: 'The full page used, the scene centred above the key strip.'},
        {key: 'tricky', label: 'Tricky', style: 'Drawn hard for an older child: the objects tiny and camouflaged in the busiest corners, fine line weight, detail to every edge', palette: 'Black ink only', framing: 'The scene filling the sheet to a narrow white margin.'},
    ],
    directions: ['a beach scene instead', 'hide twenty objects', 'a Halloween version'],
    seo: {
        title: 'Hidden pictures: picnic, bedroom, farmyard',
        metaDescription: 'Nine hidden pictures worksheets — picnic, bedroom, farmyard — ten objects hidden per scene, three difficulty levels, a key strip.',
        h1: 'Hidden pictures: picnic, bedroom and farmyard in three difficulties',
        tagline: 'Nine hidden-picture scenes, three settings, three difficulties each — a key strip shows what to find.',
        intro: 'A busy scene with ten small objects hidden in it — a toothbrush in the fence, a banana in the cloud, a sock in the tree — plus a key strip showing what to find. Large and near plain sight for a young child, disguised the classic magazine way, or tiny and camouflaged for an older kid. Clean line art, open enough to color once every object is found.',
        useCases: [
            'a car-ride activity sheet',
            'a classroom quiet-time worksheet',
            'a birthday party table activity',
            'a screen-free travel page',
        ],
        faq: [
            {q: 'What size do the pages print at?', a: 'Each page is drawn at portrait A4 proportions (832 by 1216) with a white margin, so it prints clean on standard paper.'},
            {q: 'How many objects are hidden?', a: 'Ten, listed on the key strip along the bottom of every page.'},
            {q: 'Can I get a different hidden-picture scene?', a: 'Yes — that is what the direction field on the page is for. Type a different scene and it is drawn again at all three difficulties.'},
        ],
    },
}
